import classnames from 'classnames';

// Internal dependencies
import {
  InspectorControlsAppearance,
  InspectorControlsGeneral,
  InspectorTabs,
} from '@components/inspector-tabs';
import ClassDropdown, { getBlockClassNames } from '@components/class-dropdown';
import Visibility from '@components/visibility';
import { DEFAULT_CSS_CLASS } from '@constants';
import Label from '@components/block-label';
import {
  FocusableIcon,
  FocusableIconSwitcher,
} from '@components/focusable-block-icon';
import { title } from './block.json';
import { svgAnimatorIcon } from './icons';
import './editor.scss';

// Wordpress dependencies
const {
  i18n: { __ },
  blockEditor: { useBlockProps, MediaPlaceholder },
  components: { ToolbarGroup, ToolbarButton },
  blockEditor: { BlockControls, MediaUpload, MediaUploadCheck },
} = wp;

const InspectorGeneral = ({ attributes, setAttributes }) => {
  const { blockVisibility, label } = attributes;
  return (
    <InspectorControlsGeneral>
      <Label {...{ label, setAttributes }} />
      <Visibility {...{ blockVisibility, setAttributes }} />
    </InspectorControlsGeneral>
  );
};

const InspectorAppearance = ({ attributes, setAttributes }) => {
  const { blockClassNames, enableFocusableIcon } = attributes;
  return (
    <InspectorControlsAppearance>
      <ClassDropdown
        {...{
          blockClassNames,
          setAttributes,
          blockClassPrefix: DEFAULT_CSS_CLASS.SVG_ANIMATOR,
          initialOpen: true,
        }}
      />
      <FocusableIconSwitcher
        {...{
          enableFocusableIcon,
          setAttributes,
        }}
      />
    </InspectorControlsAppearance>
  );
};

const Edit = (props) => {
  const { attributes, setAttributes } = props;
  const {
    blockVisibility,
    blockClassNames,
    media,
    isCollapsed,
    enableFocusableIcon,
  } = attributes;

  const defaultBlockCssClass = DEFAULT_CSS_CLASS.SVG_ANIMATOR;
  const classNames = getBlockClassNames(blockClassNames);

  const blockProps = useBlockProps({
    className: classnames({
      [`${defaultBlockCssClass}`]: true,
      [`${classNames}`]: classNames,
      disable: !blockVisibility,
      'is-collapsed': isCollapsed,
    }),
  });

  const placeholderUrl =
    'https://placehold.co/600x400?text=Select+an+Animation';

  return (
    <>
      <InspectorTabs>
        <InspectorGeneral {...props} />
        <InspectorAppearance {...props} />
      </InspectorTabs>
      {media?.url && (
        <BlockControls>
          <ToolbarGroup>
            <MediaUploadCheck>
              <MediaUpload
                onSelect={(file) => {
                  setAttributes({
                    media: {
                      id: file.id,
                      title: file.title,
                      filename: file.filename,
                      url: file.url,
                      fileType: file.type,
                    },
                  });
                }}
                value={media?.id}
                allowedTypes={['image/svg+xml']}
                multiple={false}
                render={({ open }) => (
                  <>
                    <>
                      <ToolbarButton
                        icon="open-folder"
                        label={__('Choose from the Media Library', 'b3rg')}
                        onClick={open}
                      ></ToolbarButton>
                      <ToolbarButton
                        icon="trash"
                        label={__('Remove', 'b3rg')}
                        onClick={() =>
                          setAttributes({
                            media: { url: null },
                          })
                        }
                      />
                    </>
                  </>
                )}
              />
            </MediaUploadCheck>
          </ToolbarGroup>
        </BlockControls>
      )}
      <div {...blockProps}>
        {enableFocusableIcon && (
          <FocusableIcon {...{ title, attributes, setAttributes }} />
        )}
        {media?.url ? (
          <>
            <div className={`${defaultBlockCssClass}__overlay`}></div>
            <object data={media.url ?? placeholderUrl} type="image/svg+xml">
              {' '}
              {__('SVG Animator', 'b3rg')}
            </object>
          </>
        ) : (
          <MediaPlaceholder
            icon={svgAnimatorIcon}
            onSelect={(file) => {
              setAttributes({
                media: {
                  id: file.id,
                  title: file.title,
                  filename: file.filename,
                  url: file.url,
                  fileType: file.type,
                },
              });
            }}
            accept="image/svg+xml"
            allowedTypes={['image/svg+xml']}
            multiple={false}
            labels={{
              title: __('SVG Animator', 'b3rg'),
              instructions: __('Display and configure SVG animations.', 'b3rg'),
            }}
          ></MediaPlaceholder>
        )}
      </div>
    </>
  );
};
export default Edit;
